import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Shield, Download, Database, FileText, UploadCloud, Play,
    CheckCircle2, AlertCircle, RefreshCw, Archive, Clock, Layers, Zap,
    XCircle, ChevronDown, ChevronUp, Eye, Lock, Bookmark, Hash, HardDrive,
    Plus, Wifi, WifiOff
} from 'lucide-react';
import { showToast } from '../../App_V3';
import { cn } from '../../components/SharedComponents';
import {
    initLocalToken,
    listBackups,
    generateBackup,
    downloadBackupFile,
    createCheckpoint,
    listCheckpoints,
    restorePreview,
    restoreCommit,
    getBackupCounts,
    isRecoverySafeHostingMode,
    SAFE_HOSTING_MESSAGE,
    RECOVERY_AUTH_REQUIRED_MESSAGE,
    type BackupItem,
    type RuntimeState,
    type AuditEntry,
    type CheckpointItem,
    type RestoreReport,
} from '../../lib/recovery/recoveryRuntime';

// --- Helpers ---
const formatSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (d: string): string => {
    try { return new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return d; }
};

const TYPE_STYLES: Record<string, { bg: string; text: string; border: string; icon: React.ElementType }> = {
    JSON: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/20', icon: Database },
    EXCEL: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20', icon: FileText },
    ZIP: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', border: 'border-indigo-500/20', icon: Archive },
    INCREMENTAL: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20', icon: Layers },
    CHECKPOINT: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20', icon: Bookmark },
};

const RESTORE_TABLES = [
    { key: 'gov_indicators', label: 'Indicadores' },
    { key: 'gov_work_plans', label: 'Planes de Trabajo' },
    { key: 'gov_work_plan_sections', label: 'Secciones' },
    { key: 'gov_work_plan_items', label: 'Work Items' },
    { key: 'gov_vicepresidencies', label: 'Vicepresidencias' },
    { key: 'gov_commissions', label: 'Comisiones' },
];

export default function BackupRecoveryCenter({ user }: { user: any }) {
    const isFirebaseHosting = isRecoverySafeHostingMode();

    // --- State ---
    const [backups, setBackups] = useState<BackupItem[]>([]);
    const [runtimeState, setRuntimeState] = useState<RuntimeState>({ safeMode: 'ACTIVE', reconfigMode: 'ACTIVE' });
    const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState<string | null>(null);
    const [restoreReport, setRestoreReport] = useState<RestoreReport | null>(null);
    const [simulating, setSimulating] = useState(false);
    const [selectedTables, setSelectedTables] = useState<string[]>(RESTORE_TABLES.map(t => t.key));
    const [importedFile, setImportedFile] = useState<File | null>(null);
    const [activeTab, setActiveTab] = useState<'dashboard' | 'timeline' | 'restore' | 'checkpoints' | 'guide'>('dashboard');
    const [confirmText, setConfirmText] = useState('');
    const [showCommitModal, setShowCommitModal] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [checkpoints, setCheckpoints] = useState<CheckpointItem[]>([]);
    const [cpName, setCpName] = useState('');
    const [cpNotes, setCpNotes] = useState('');
    const [creatingCp, setCreatingCp] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    // --- Inicialización del bridge ---
    const initBridge = useCallback(async () => {
        if (isFirebaseHosting) {
            setIsConnected(false);
            return false;
        }
        try {
            await initLocalToken();
            setIsConnected(true);
            return true;
        } catch (e: any) {
            console.warn('[RecoveryBridge] Runtime local no disponible:', e.message);
            setIsConnected(false);
            return false;
        }
    }, [isFirebaseHosting]);

    // --- Data Fetching REAL ---
    const fetchBackups = useCallback(async () => {
        if (isFirebaseHosting) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const connected = await initBridge();
            if (!connected) {
                showToast(RECOVERY_AUTH_REQUIRED_MESSAGE, 'error');
                return;
            }
            const data = await listBackups();
            setBackups(data.backups || []);
            if (data.runtimeState) setRuntimeState(data.runtimeState);
            if (data.auditLog) setAuditLog(data.auditLog);
        } catch (e: any) {
            showToast('Error cargando backups: ' + e.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [initBridge]);

    const fetchCheckpoints = useCallback(async () => {
        if (isFirebaseHosting) return;
        try {
            const data = await listCheckpoints();
            setCheckpoints(data.checkpoints || []);
        } catch (e: any) {
            console.error('[Checkpoints] Error:', e.message);
        }
    }, []);

    useEffect(() => {
        fetchBackups();
        fetchCheckpoints();
    }, [fetchBackups, fetchCheckpoints]);

    // --- Actions REALES ---
    const handleGenerate = async (type: 'json' | 'excel' | 'incremental') => {
        if (isFirebaseHosting) {
            showToast(SAFE_HOSTING_MESSAGE, 'warning');
            return;
        }
        try {
            setGenerating(type);
            const result = await generateBackup(type);
            showToast(result.message || `Backup ${type} completado`, 'success');
            // Refrescar datos reales
            await fetchBackups();
        } catch (e: any) {
            showToast(e.message, 'error');
        } finally {
            setGenerating(null);
        }
    };

    const handleDownload = async (item: BackupItem) => {
        if (isFirebaseHosting) {
            showToast(SAFE_HOSTING_MESSAGE, 'warning');
            return;
        }
        try {
            showToast(`Descargando ${item.name}...`, 'info');
            await downloadBackupFile(item.path, item.name);
            showToast(`Descarga de ${item.name} completada`, 'success');
        } catch (e: any) {
            showToast('Error descargando: ' + e.message, 'error');
        }
    };

    const handleCreateCheckpoint = async () => {
        if (isFirebaseHosting) {
            showToast(SAFE_HOSTING_MESSAGE, 'warning');
            return;
        }
        if (!cpName.trim()) {
            showToast('Ingresa un nombre para el checkpoint', 'warning');
            return;
        }
        try {
            setCreatingCp(true);
            const result = await createCheckpoint(cpName, cpNotes);
            showToast(result.message || 'Checkpoint creado', 'success');
            setCpName('');
            setCpNotes('');
            await fetchCheckpoints();
            await fetchBackups();
        } catch (e: any) {
            showToast('Error creando checkpoint: ' + e.message, 'error');
        } finally {
            setCreatingCp(false);
        }
    };

    const runRestore = async (mode: 'dry-run' | 'commit') => {
        if (isFirebaseHosting) {
            showToast(SAFE_HOSTING_MESSAGE, 'warning');
            return;
        }
        if (mode === 'commit') {
            if (confirmText !== 'RESTAURAR') {
                showToast('Escribe RESTAURAR para confirmar', 'warning');
                return;
            }
            setShowCommitModal(false);
            setConfirmText('');
        }
        setSimulating(true);
        if (mode === 'dry-run') setRestoreReport(null);
        try {
            // RBAC HOTFIX v18.18.2: restoreCommit requiere userRole explícito para guard de rol.
            // restorePreview (dry-run) no modifica datos — no necesita guard de rol aquí.
            const data = mode === 'dry-run'
                ? await restorePreview(selectedTables)
                : await restoreCommit(selectedTables, user?.role);
            if (data.report) setRestoreReport(data.report);
            showToast(mode === 'dry-run' ? 'Simulación completada' : 'Restauración ejecutada', mode === 'dry-run' ? 'info' : 'success');
            if (mode === 'commit') {
                await fetchBackups();
            }
        } catch (e: any) {
            showToast('Error en restore: ' + e.message, 'error');
        } finally {
            setSimulating(false);
        }
    };

    const handleFileDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (isFirebaseHosting) {
            showToast(SAFE_HOSTING_MESSAGE, 'warning');
            return;
        }
        const file = e.dataTransfer.files[0];
        if (file && (file.name.endsWith('.zip') || file.name.endsWith('.json'))) {
            setImportedFile(file);
            showToast(`Archivo cargado: ${file.name}`, 'success');
        } else {
            showToast('Solo se aceptan archivos .zip o .json', 'warning');
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isFirebaseHosting) {
            showToast(SAFE_HOSTING_MESSAGE, 'warning');
            return;
        }
        const file = e.target.files?.[0];
        if (file) {
            setImportedFile(file);
            showToast(`Archivo cargado: ${file.name}`, 'success');
        }
    };

    const toggleTable = (key: string) => {
        setSelectedTables(prev =>
            prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
        );
    };

    // --- Derived (CONTADORES REALES) ---
    const counts = getBackupCounts(backups);
    const lastBackup = backups.length > 0 ? [...backups].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0] : null;

    const unifiedTimeline = React.useMemo(() => {
        const items: any[] = [];
        backups.forEach(b => items.push({ type: b.type, date: new Date(b.date), data: b, originalType: 'backup' }));
        checkpoints.forEach(c => items.push({ type: 'CHECKPOINT', date: new Date(c.date), data: c, originalType: 'checkpoint' }));
        auditLog.filter(a => a.action?.includes('RESTORE') || a.action?.includes('DRY')).forEach(a => {
            items.push({ type: 'AUDIT', date: new Date(a.date), data: a, originalType: 'audit' });
        });
        return items.sort((a, b) => b.date.getTime() - a.date.getTime());
    }, [backups, checkpoints, auditLog]);

    const getRiskLevel = (report: RestoreReport | null) => {
        if (!report) return null;
        let conflicts = 0;
        let toUpsert = 0;
        Object.values(report.tables).forEach(t => { conflicts += (t.conflicts || 0); toUpsert += (t.to_upsert || 0); });
        if (conflicts > 0) return { level: 'HIGH', label: '🔴 Riesgo Alto (Conflictos de Integridad)', color: 'text-red-500' };
        if (toUpsert > 100) return { level: 'MEDIUM', label: '🟡 Modificación Masiva (Revisar)', color: 'text-amber-500' };
        return { level: 'LOW', label: '🟢 Seguro (Sin Conflictos)', color: 'text-emerald-500' };
    };
    
    const risk = getRiskLevel(restoreReport);

    // --- RENDER ---
    return (
        <div className="space-y-10 pb-24 font-['Outfit']">
            {/* ==================== HEADER ==================== */}
            <header className="relative flex flex-col md:flex-row justify-between items-start md:items-end gap-8 bg-slate-950/40 p-8 md:p-12 rounded-[2rem] md:rounded-[4rem] border border-white/5 overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.5)] backdrop-blur-xl">
                <div className="absolute top-0 right-0 w-[40%] h-[40%] bg-emerald-500/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-indigo-500/10 blur-[100px] rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />

                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-4 md:mb-6 flex-wrap">
                        <span className="bg-emerald-500/10 text-emerald-500 px-4 md:px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-500/20 flex items-center gap-2">
                            <Shield size={12} /> FORENSIC BACKUP ENGINE
                        </span>
                        <span className="bg-slate-900/80 text-white/40 px-4 md:px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest italic border border-white/5">v18.18.0-MULTIAPP-HARDLOCK-LOCAL</span>
                        <span className={cn(
                            'px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border',
                            isConnected
                                ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                                : 'bg-red-500/10 text-red-400 border-red-500/20'
                        )}>
                            {isConnected ? <Wifi size={10} /> : <WifiOff size={10} />}
                            {isConnected ? 'BRIDGE ONLINE' : 'BRIDGE OFFLINE'}
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-7xl font-black text-white tracking-tighter uppercase leading-none italic">
                        Recovery <span className="text-emerald-500">Center</span>
                    </h1>
                    <p className="text-slate-500 text-[11px] md:text-[12px] font-black uppercase tracking-[0.3em] md:tracking-[0.4em] mt-4 md:mt-6 opacity-60">Centro de Backups y Restauración Segura</p>
                </div>

                <div className="relative z-10 flex gap-4 flex-wrap">
                    <div className="bg-slate-900 border border-white/10 px-6 py-4 md:p-8 rounded-2xl md:rounded-[2.5rem] flex flex-col items-center justify-center min-w-[120px] md:min-w-[160px]">
                        <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1">SAFE MODE</p>
                        <p className="text-xl md:text-3xl font-black text-white italic">v3</p>
                        <p className="text-[9px] text-emerald-500/60 mt-1 tracking-widest">ACTIVE</p>
                    </div>
                    <div className="bg-slate-900 border border-white/10 px-6 py-4 md:p-8 rounded-2xl md:rounded-[2.5rem] flex flex-col items-center justify-center min-w-[120px] md:min-w-[160px]">
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">RECONFIG</p>
                        <p className="text-xl md:text-3xl font-black text-white italic">ON</p>
                        <p className="text-[9px] text-indigo-400/60 mt-1 tracking-widest">ACTIVE</p>
                    </div>
                </div>
            </header>

            {/* ==================== TABS ==================== */}
            <div className="flex gap-2 p-2 bg-slate-950 rounded-2xl border border-white/5 shadow-2xl w-fit flex-wrap">
                {(['dashboard', 'timeline', 'checkpoints', 'restore', 'guide'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={cn(
                            'px-5 md:px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all min-h-[44px]',
                            activeTab === tab ? 'bg-white text-black shadow-xl' : 'text-white/30 hover:text-white hover:bg-white/5'
                        )}
                    >
                        {tab === 'dashboard' ? '🛡️ Panel' : tab === 'timeline' ? '⏱️ Timeline' : tab === 'checkpoints' ? '📌 Checkpoints' : tab === 'restore' ? '🔄 Restaurar' : '📘 Guía Operativa'}
                    </button>
                ))}
            </div>

            {isFirebaseHosting && (
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 md:p-6 rounded-2xl flex items-start gap-4">
                    <AlertCircle className="text-amber-500 shrink-0 mt-1" size={24} />
                    <div className="text-amber-500">
                        <p className="font-bold uppercase tracking-widest text-sm mb-1">Safe Hosting Mode (Producción)</p>
                        <p className="text-[11px] opacity-80 leading-relaxed">
                            {SAFE_HOSTING_MESSAGE}. La generación de JSON/ZIP, respaldos incrementales y restauración directa están bloqueadas por seguridad en Firebase Hosting.
                        </p>
                    </div>
                </div>
            )}

            {/* ==================== DASHBOARD TAB ==================== */}
            {activeTab === 'dashboard' && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Acciones Rápidas */}
                    <aside className="lg:col-span-1 space-y-8">
                        <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[3rem] p-6 md:p-8 space-y-5 backdrop-blur-md">
                            <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-3">
                                <Zap size={18} className="text-emerald-500" />
                                Acciones Rápidas
                            </h3>

                            {[
                                { type: 'json' as const, label: 'Generar JSON Full', desc: 'Respaldo maestro completo', icon: Database, color: 'emerald' },
                                { type: 'incremental' as const, label: 'Generar Incremental', desc: 'Solo cambios recientes', icon: Layers, color: 'indigo' },
                                { type: 'excel' as const, label: 'Exportar Excel', desc: 'Reporte ejecutivo auditable', icon: FileText, color: 'amber' },
                            ].map(action => (
                                <button
                                    key={action.type}
                                    onClick={() => handleGenerate(action.type)}
                                    disabled={generating !== null || isFirebaseHosting}
                                    className={cn(
                                        'w-full flex items-center justify-between p-4 rounded-2xl bg-slate-950 border border-white/5 transition-all group disabled:opacity-50 min-h-[56px]',
                                        `hover:bg-${action.color}-500/10 hover:border-${action.color}-500/30`
                                    )}
                                >
                                    <div className="text-left">
                                        <span className={cn('block text-[10px] font-black uppercase tracking-widest text-white', `group-hover:text-${action.color}-400`)}>{action.label}</span>
                                        <span className="block text-[8px] text-slate-500 uppercase tracking-widest mt-0.5">{action.desc}</span>
                                    </div>
                                    {generating === action.type
                                        ? <RefreshCw size={16} className={`animate-spin text-${action.color}-500`} />
                                        : <action.icon size={16} className={`text-slate-500 group-hover:text-${action.color}-400`} />
                                    }
                                </button>
                            ))}
                        </div>
                    </aside>

                    {/* Stats Grid — CONTADORES REALES */}
                    <div className="lg:col-span-3 space-y-8">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            {[
                                { label: 'JSON Files', count: counts.json, color: 'emerald', icon: Database },
                                { label: 'Excel Exports', count: counts.excel, color: 'amber', icon: FileText },
                                { label: 'Incrementales', count: counts.incremental, color: 'blue', icon: Layers },
                                { label: 'Recovery ZIP', count: counts.zip, color: 'indigo', icon: Archive },
                                { label: 'Checkpoints', count: counts.checkpoint, color: 'purple', icon: Bookmark },
                            ].map(stat => (
                                <div key={stat.label} className="bg-slate-900/40 border border-white/5 rounded-2xl md:rounded-3xl p-5 md:p-6 backdrop-blur-md">
                                    <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center mb-3', `bg-${stat.color}-500/10 text-${stat.color}-500 border border-${stat.color}-500/20`)}>
                                        <stat.icon size={18} />
                                    </div>
                                    <p className="text-3xl md:text-4xl font-black text-white italic">{loading ? '—' : stat.count}</p>
                                    <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mt-1">{stat.label}</p>
                                </div>
                            ))}
                        </div>

                        {lastBackup && (
                            <div className="bg-slate-900/40 border border-white/5 rounded-2xl md:rounded-3xl p-6 md:p-8 backdrop-blur-md">
                                <h4 className="text-white font-black uppercase text-sm tracking-widest mb-4 flex items-center gap-2"><Clock size={16} className="text-emerald-500" /> Último Backup</h4>
                                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
                                    <div>
                                        <p className="text-white font-bold text-sm">{lastBackup.name}</p>
                                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">{formatDate(lastBackup.date)} • {formatSize(lastBackup.size)} • {lastBackup.type}</p>
                                    </div>
                                    <button
                                        onClick={() => handleDownload(lastBackup)}
                                        className="px-5 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-xl transition-all border border-emerald-500/20 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest min-h-[44px]"
                                    >
                                        <Download size={14} /> Descargar
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Audit Log Preview */}
                        {auditLog.length > 0 && (
                            <div className="bg-slate-900/40 border border-white/5 rounded-2xl md:rounded-3xl p-6 md:p-8 backdrop-blur-md">
                                <h4 className="text-white font-black uppercase text-sm tracking-widest mb-4 flex items-center gap-2"><Hash size={16} className="text-indigo-400" /> Últimas Operaciones</h4>
                                <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar">
                                    {auditLog.slice(0, 10).map((entry, idx) => (
                                        <div key={idx} className="flex items-center gap-3 text-[10px] py-2 border-b border-white/5 last:border-0">
                                            <span className="text-slate-500 font-mono shrink-0">{formatDate(entry.date)}</span>
                                            <span className={cn(
                                                'px-2 py-0.5 rounded-full font-black uppercase tracking-widest border shrink-0',
                                                entry.action?.includes('ERROR') ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                            )}>{entry.action}</span>
                                            <span className="text-slate-400 truncate">{entry.user || 'SYSTEM'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ==================== TIMELINE TAB ==================== */}
            {activeTab === 'timeline' && (
                <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[4rem] p-8 md:p-12 backdrop-blur-md">
                    <div className="flex items-center justify-between mb-10">
                        <div>
                            <h3 className="text-white font-black uppercase text-xl tracking-widest">Recovery Timeline</h3>
                            <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest opacity-60">Historial forense unificado</p>
                        </div>
                        <button onClick={() => { fetchBackups(); fetchCheckpoints(); }} className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-emerald-500 hover:bg-white/10 transition-all border border-white/10 min-w-[48px] min-h-[48px]">
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>

                    <div className="relative border-l border-white/10 ml-6 space-y-8 pb-10">
                        {unifiedTimeline.map((item, idx) => {
                            let style = TYPE_STYLES[item.type] || TYPE_STYLES.JSON;
                            if (item.type === 'AUDIT') style = { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/20', icon: Shield };
                            const Icon = style.icon;

                            return (
                                <div key={idx} className="relative pl-8 group">
                                    <div className={cn('absolute -left-[18px] top-1 w-9 h-9 rounded-full flex items-center justify-center border-4 border-slate-950', style.bg, style.text)}>
                                        <Icon size={14} />
                                    </div>
                                    <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-5 hover:border-white/20 transition-all">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className={cn('text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border', style.bg, style.text, style.border)}>{item.type}</span>
                                            <span className="text-[9px] text-slate-500 font-mono">{formatDate(item.data.date)}</span>
                                        </div>
                                        
                                        {item.originalType === 'backup' && (
                                            <>
                                                <p className="text-white font-bold text-sm mb-1">{item.data.name}</p>
                                                <p className="text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                                    <span>{formatSize(item.data.size)}</span>
                                                    {item.data.path && <span className="font-mono text-slate-600 truncate">{item.data.path}</span>}
                                                </p>
                                                <button onClick={() => handleDownload(item.data)} className="mt-3 text-[10px] flex items-center gap-1 text-emerald-500 hover:text-emerald-400 font-black uppercase tracking-widest">
                                                    <Download size={12}/> Descargar Archivo
                                                </button>
                                            </>
                                        )}
                                        
                                        {item.originalType === 'checkpoint' && (
                                            <>
                                                <p className="text-white font-bold text-sm mb-1">{item.data.name}</p>
                                                <div className="flex flex-col gap-1 text-[10px] text-slate-500 uppercase tracking-widest mt-2">
                                                    {item.data.metadata?.notes && <p className="italic text-slate-400">"{item.data.metadata.notes}"</p>}
                                                    <p className="font-mono">Hash: {item.data.metadata?.hash || 'N/A'}</p>
                                                </div>
                                            </>
                                        )}

                                        {item.originalType === 'audit' && (
                                            <>
                                                <p className="text-red-400 font-bold text-sm mb-1">{item.data.action}</p>
                                                <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">
                                                    Ejecutado por: <span className="text-white/60">{item.data.user || 'SYSTEM'}</span>
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {unifiedTimeline.length === 0 && !loading && (
                            <div className="text-center py-10 pl-8">
                                <p className="text-slate-500 font-black uppercase tracking-widest text-sm">No hay eventos en el timeline</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ==================== CHECKPOINTS TAB ==================== */}
            {activeTab === 'checkpoints' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Crear Checkpoint */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[3rem] p-6 md:p-8 space-y-5 backdrop-blur-md">
                            <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-3">
                                <Plus size={18} className="text-purple-500" />
                                Crear Checkpoint
                            </h3>
                            <input
                                type="text"
                                value={cpName}
                                onChange={e => setCpName(e.target.value)}
                                placeholder="Nombre del checkpoint..."
                                className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-purple-500/50 focus:outline-none transition-all min-h-[44px]"
                            />
                            <textarea
                                value={cpNotes}
                                onChange={e => setCpNotes(e.target.value)}
                                placeholder="Notas opcionales..."
                                rows={3}
                                className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-purple-500/50 focus:outline-none transition-all resize-none"
                            />
                            <button
                                onClick={handleCreateCheckpoint}
                                disabled={creatingCp || !cpName.trim() || isFirebaseHosting}
                                className="w-full bg-purple-600 hover:bg-purple-500 text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-2xl transition-all shadow-[0_10px_20px_rgba(147,51,234,0.3)] flex items-center justify-center gap-2 disabled:opacity-50 min-h-[48px]"
                            >
                                {creatingCp ? <RefreshCw size={14} className="animate-spin" /> : <Bookmark size={14} />}
                                Crear Checkpoint
                            </button>
                        </div>
                    </div>

                    {/* Lista de Checkpoints */}
                    <div className="lg:col-span-2 bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[4rem] p-8 md:p-12 backdrop-blur-md">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-white font-black uppercase text-xl tracking-widest flex items-center gap-3">
                                <Bookmark size={20} className="text-purple-500" />
                                Checkpoints Guardados
                            </h3>
                            <button onClick={fetchCheckpoints} className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-purple-500 hover:bg-white/10 transition-all border border-white/10 min-w-[48px] min-h-[48px]">
                                <RefreshCw size={18} />
                            </button>
                        </div>

                        <div className="space-y-3 max-h-[50vh] overflow-y-auto custom-scrollbar">
                            {checkpoints.length === 0 && (
                                <div className="text-center py-16">
                                    <Bookmark size={48} className="text-slate-700 mx-auto mb-4" />
                                    <p className="text-slate-500 font-black uppercase tracking-widest text-sm">No hay checkpoints</p>
                                </div>
                            )}
                            {checkpoints.map((cp, idx) => (
                                <div key={idx} className="p-5 bg-slate-950/60 border border-white/5 rounded-2xl hover:border-purple-500/30 transition-all">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-white font-black text-sm">{cp.name}</span>
                                        <span className="text-[9px] text-slate-500 uppercase tracking-widest">{formatDate(cp.date)} • {formatSize(cp.size)}</span>
                                    </div>
                                    {cp.metadata && (
                                        <div className="flex items-center gap-3 flex-wrap mt-2">
                                            <span className="text-[8px] bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded-full font-black uppercase tracking-widest">{cp.metadata.version}</span>
                                            {cp.metadata.notes && <span className="text-[9px] text-slate-400 italic">{cp.metadata.notes}</span>}
                                            <span className="text-[8px] text-slate-500 font-mono">{cp.metadata.hash?.slice(0, 12)}...</span>
                                        </div>
                                    )}
                                    <div className="flex gap-2 mt-3">
                                        <button
                                            onClick={() => handleDownload(cp as any)}
                                            className="px-4 py-2 bg-white/5 hover:bg-purple-500/20 text-slate-400 hover:text-purple-400 rounded-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-1 min-h-[44px]"
                                        >
                                            <Download size={12} /> Descargar
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== RESTORE TAB ==================== */}
            {activeTab === 'restore' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Import & Configure */}
                    <div className="lg:col-span-1 space-y-8">
                        {/* Importar */}
                        <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[3rem] p-6 md:p-8 space-y-5 backdrop-blur-md">
                            <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-3">
                                <UploadCloud size={18} className="text-indigo-500" />
                                Importar Backup
                            </h3>

                            <div
                                onDragOver={e => e.preventDefault()}
                                onDrop={handleFileDrop}
                                onClick={() => isFirebaseHosting ? showToast(SAFE_HOSTING_MESSAGE, 'warning') : fileRef.current?.click()}
                                className={cn(
                                    'border-2 border-dashed rounded-2xl p-6 md:p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all min-h-[120px]',
                                    importedFile ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 hover:border-indigo-500/50 hover:bg-indigo-500/5',
                                    isFirebaseHosting && 'opacity-50'
                                )}
                            >
                                <input ref={fileRef} type="file" className="hidden" accept=".zip,.json" onChange={handleFileSelect} disabled={isFirebaseHosting} />
                                {importedFile ? (
                                    <>
                                        <CheckCircle2 size={28} className="text-emerald-500 mb-3" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">{importedFile.name}</span>
                                        <span className="text-[9px] text-emerald-500/60 mt-1">{formatSize(importedFile.size)}</span>
                                    </>
                                ) : (
                                    <>
                                        <Archive size={28} className="text-indigo-500/50 mb-3" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/50">Arrastrar .zip o .json</span>
                                        <span className="text-[9px] text-slate-600 mt-1">o click para seleccionar</span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Selección granular (Desactivada) */}
                        <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[3rem] p-6 md:p-8 space-y-4 backdrop-blur-md opacity-50 pointer-events-none relative">
                            <div className="absolute inset-0 z-10 bg-slate-950/20 backdrop-blur-[2px] rounded-[3rem] flex items-center justify-center">
                                <span className="bg-amber-500/20 text-amber-500 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border border-amber-500/30">Próximamente Fase R6</span>
                            </div>
                            <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-3">
                                <Lock size={18} className="text-amber-500" />
                                Restore Granular
                            </h3>
                            <p className="text-[10px] text-slate-500 uppercase tracking-widest">Selecciona las tablas a restaurar:</p>

                            {RESTORE_TABLES.map(t => (
                                <label key={t.key} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                                    <div className={cn(
                                        'w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all shrink-0',
                                        selectedTables.includes(t.key) ? 'bg-emerald-500 border-emerald-500' : 'border-white/20 group-hover:border-white/40'
                                    )}>
                                        {selectedTables.includes(t.key) && <CheckCircle2 size={12} className="text-white" />}
                                    </div>
                                    <span className="text-[11px] text-white/70 font-bold uppercase tracking-widest group-hover:text-white transition-colors">{t.label}</span>
                                </label>
                            ))}
                        </div>

                        {/* Botones de Restore */}
                        <div className="space-y-3">
                            <button
                                onClick={() => runRestore('dry-run')}
                                disabled={simulating || isFirebaseHosting}
                                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-2xl transition-all shadow-[0_10px_20px_rgba(79,70,229,0.3)] flex items-center justify-center gap-2 disabled:opacity-50 min-h-[48px]"
                            >
                                {simulating ? <RefreshCw size={14} className="animate-spin" /> : <Eye size={14} />}
                                1. Analizar e Inspeccionar (Dry-Run)
                            </button>

                            <button
                                onClick={() => setShowCommitModal(true)}
                                disabled={simulating || !restoreReport || restoreReport.mode !== 'DRY_RUN' || isFirebaseHosting}
                                className="w-full bg-amber-600 hover:bg-amber-500 text-white font-black text-[10px] uppercase tracking-widest py-4 rounded-2xl transition-all shadow-[0_10px_20px_rgba(217,119,6,0.2)] flex items-center justify-center gap-2 disabled:opacity-50 min-h-[48px]"
                            >
                                <Play size={14} /> 2. Ejecutar Restore Real
                            </button>
                        </div>
                        
                        {/* Modal de Doble Confirmación */}
                        {showCommitModal && (
                            <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
                                <div className="bg-slate-900 border border-red-500/30 rounded-3xl p-8 max-w-md w-full shadow-[0_0_50px_rgba(239,68,68,0.2)]">
                                    <h3 className="text-red-500 font-black uppercase text-xl tracking-widest mb-4 flex items-center gap-3">
                                        <AlertCircle size={24} /> Confirmación de Seguridad
                                    </h3>
                                    <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                                        Estás a punto de sobreescribir la Base de Datos Productiva. Esta acción modificará registros operativos. Para confirmar tu intención forense, escribe <strong className="text-white bg-slate-800 px-2 rounded">RESTAURAR</strong>.
                                    </p>
                                    <input 
                                        type="text" 
                                        value={confirmText}
                                        onChange={e => setConfirmText(e.target.value)}
                                        placeholder="Escribe RESTAURAR"
                                        className="w-full bg-slate-950 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 font-black tracking-widest text-center mb-6 focus:outline-none focus:border-red-500 uppercase"
                                    />
                                    <div className="flex gap-3">
                                        <button onClick={() => {setShowCommitModal(false); setConfirmText('');}} className="flex-1 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest">Cancelar</button>
                                        <button onClick={() => runRestore('commit')} disabled={confirmText !== 'RESTAURAR'} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest disabled:opacity-50 transition-all">RESTORE COMMIT</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Restore Report */}
                    <div className="lg:col-span-2 bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[4rem] p-8 md:p-12 backdrop-blur-md">
                        <h3 className="text-white font-black uppercase text-xl tracking-widest mb-8 flex items-center gap-3">
                            <HardDrive size={20} className="text-emerald-500" />
                            Reporte de Restauración
                        </h3>

                        {!restoreReport && !simulating && (
                            <div className="text-center py-20">
                                <Shield size={48} className="text-slate-700 mx-auto mb-4" />
                                <p className="text-slate-500 font-black uppercase tracking-widest text-sm">Ejecuta un Dry-Run para ver el reporte</p>
                                <p className="text-[10px] text-slate-600 mt-2 max-w-sm mx-auto leading-relaxed">
                                    El motor comparará el backup contra la BD actual, detectará cambios, conflictos y protecciones antes de ejecutar nada.
                                </p>
                            </div>
                        )}

                        {simulating && (
                            <div className="text-center py-20">
                                <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mx-auto mb-6" />
                                <p className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em] animate-pulse">Analizando integridad forense...</p>
                            </div>
                        )}

                        {restoreReport && !simulating && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between mb-8 flex-wrap gap-4 border-b border-white/10 pb-6">
                                    <div className="flex items-center gap-4 flex-wrap">
                                        <span className={cn(
                                            'px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border',
                                            restoreReport.mode === 'DRY_RUN'
                                                ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                                                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                        )}>
                                            {restoreReport.mode === 'DRY_RUN' ? '🔍 Simulación' : '✅ Ejecutado'}
                                        </span>
                                        <span className="text-[10px] text-slate-500 font-mono bg-slate-950 px-3 py-1.5 rounded-lg border border-white/5">{formatDate(restoreReport.timestamp)}</span>
                                    </div>
                                    
                                    {/* SEMÁFORO VISUAL */}
                                    {risk && (
                                        <div className={cn('px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border bg-slate-950/50 flex items-center gap-2', risk.color, risk.color.replace('text-', 'border-').replace('500', '500/30'))}>
                                            <Shield size={14} /> {risk.label}
                                        </div>
                                    )}
                                </div>

                                {restoreReport.tables && Object.entries(restoreReport.tables).map(([table, info]) => (
                                    <div key={table} className="bg-slate-950/60 border border-white/5 rounded-2xl p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-white font-black text-xs uppercase tracking-widest">{table.replace('gov_', '')}</span>
                                            <span className={cn(
                                                'text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border',
                                                info.status === 'SKIPPED' ? 'bg-slate-500/10 text-slate-400 border-slate-500/20' :
                                                info.status === 'ANALYZED' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' :
                                                info.status === 'RESTORED' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                                'bg-red-500/10 text-red-400 border-red-500/20'
                                            )}>{info.status}</span>
                                        </div>
                                        {info.source_count !== undefined && (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                                                <div className="text-center">
                                                    <p className="text-lg font-black text-white">{info.source_count}</p>
                                                    <p className="text-[8px] text-slate-500 uppercase tracking-widest">Source</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-lg font-black text-white">{info.current_count}</p>
                                                    <p className="text-[8px] text-slate-500 uppercase tracking-widest">Actual</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className={cn('text-lg font-black', (info.to_upsert || 0) > 0 ? 'text-amber-400' : 'text-white')}>{info.to_upsert}</p>
                                                    <p className="text-[8px] text-slate-500 uppercase tracking-widest">A Escribir</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className={cn('text-lg font-black', (info.conflicts || 0) > 0 ? 'text-red-400' : 'text-white')}>{info.conflicts}</p>
                                                    <p className="text-[8px] text-slate-500 uppercase tracking-widest">Conflictos</p>
                                                </div>
                                            </div>
                                        )}
                                        {info.conflict_details && info.conflict_details.length > 0 && (
                                            <div className="mt-3 bg-red-500/5 border border-red-500/10 rounded-xl p-3">
                                                <p className="text-[9px] text-red-400 font-black uppercase tracking-widest mb-2">Conflictos Detectados:</p>
                                                {info.conflict_details.map((c: any, i: number) => (
                                                    <p key={i} className="text-[9px] text-red-300/80">ID: {c.id} — {c.reason}</p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ==================== GUIDE TAB ==================== */}
            {activeTab === 'guide' && (
                <div className="bg-slate-900/40 border border-white/5 rounded-[2rem] md:rounded-[4rem] p-8 md:p-12 backdrop-blur-md space-y-12">
                    <div>
                        <h3 className="text-white font-black uppercase text-2xl tracking-widest mb-2 flex items-center gap-3">
                            <FileText size={24} className="text-emerald-500" />
                            Guía Operativa de Recuperación
                        </h3>
                        <p className="text-slate-400 text-sm max-w-3xl leading-relaxed">
                            Esta sección explica el uso correcto del motor forense. Entiende qué hace cada respaldo, sus diferencias y buenas prácticas para prevenir la pérdida total de información.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Tipos de Backups */}
                        <div className="space-y-6">
                            <div className="bg-slate-950/60 p-6 rounded-3xl border border-white/5 hover:border-emerald-500/30 transition-all">
                                <h4 className="text-emerald-400 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><Database size={16} /> 1. Generar JSON Full</h4>
                                <p className="text-slate-300 text-xs leading-relaxed mb-3">
                                    Este es el <strong>respaldo maestro del sistema</strong>. Genera una copia binaria, íntegra y completa de toda la Base de Datos (VP, Comisiones, Planes, Indicadores, Historial). Preserva IDs reales, metadatos y estado (activo/archivado).
                                </p>
                                <ul className="text-[10px] text-slate-500 space-y-1 ml-4 list-disc">
                                    <li><strong>Cuándo usarlo:</strong> Diario o antes de cualquier mantenimiento.</li>
                                    <li><strong>Tamaño esperado:</strong> Alto.</li>
                                    <li><strong>Nota:</strong> Debe descargarse periódicamente al equipo local.</li>
                                </ul>
                            </div>

                            <div className="bg-slate-950/60 p-6 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                                <h4 className="text-indigo-400 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><Layers size={16} /> 2. Generar Incremental</h4>
                                <p className="text-slate-300 text-xs leading-relaxed mb-3">
                                    Solo guarda los cambios realizados desde el último snapshot. Detecta modificaciones, adiciones o archivados. <em>Los incrementales complementan al JSON Full.</em>
                                </p>
                                <ul className="text-[10px] text-slate-500 space-y-1 ml-4 list-disc">
                                    <li><strong>Cuándo usarlo:</strong> Ideal para auditoría diaria continua sin duplicar información.</li>
                                    <li><strong>Ventaja:</strong> Ayuda en recuperación parcial sin pisar toda la base.</li>
                                </ul>
                            </div>

                            <div className="bg-slate-950/60 p-6 rounded-3xl border border-white/5 hover:border-amber-500/30 transition-all">
                                <h4 className="text-amber-400 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><FileText size={16} /> 3. Exportar Excel</h4>
                                <p className="text-slate-300 text-xs leading-relaxed mb-3">
                                    Genera un reporte <strong>EJECUTIVO</strong> normalizado para revisión humana, análisis o entrega como evidencia auditable.
                                </p>
                                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-500/80 p-3 rounded-xl text-[10px] font-bold mt-2">
                                    ⚠️ El Excel es operativo/humano, NO el respaldo maestro. No es útil para un recovery binario completo.
                                </div>
                            </div>
                        </div>

                        {/* Conceptos Avanzados */}
                        <div className="space-y-6">
                            <div className="bg-slate-950/60 p-6 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                                <h4 className="text-purple-400 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><Bookmark size={16} /> 4. Checkpoints</h4>
                                <p className="text-slate-300 text-xs leading-relaxed mb-2">
                                    Congelan un punto en el tiempo antes de operaciones peligrosas. Guardan el estado del <em>Runtime</em>, metadatos y un hash.
                                </p>
                                <ul className="text-[10px] text-slate-500 space-y-1 ml-4 list-disc">
                                    <li>Útiles como "puntos de guardado" antes de ejecutar un Restore o un pase a producción.</li>
                                </ul>
                            </div>

                            <div className="bg-slate-950/60 p-6 rounded-3xl border border-white/5 hover:border-sky-500/30 transition-all">
                                <h4 className="text-sky-400 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><Archive size={16} /> 5. Recovery ZIP</h4>
                                <p className="text-slate-300 text-xs leading-relaxed mb-2">
                                    Paquete portátil (ZIP) que consolida automáticamente JSON Full, incrementales, excel y metadata. Ideal para enviarlo a la nube externa, guardarlo en USB o Disaster Recovery.
                                </p>
                            </div>

                            <div className="bg-slate-950/60 p-6 rounded-3xl border border-white/5 hover:border-red-500/30 transition-all">
                                <h4 className="text-red-400 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><Play size={16} /> 6. Restore / Recuperación</h4>
                                <p className="text-slate-300 text-xs leading-relaxed mb-2">
                                    <strong>Preview (Dry-Run):</strong> Analiza la integridad, conflictos y qué pasaría sin escribir nada en la BD.<br/>
                                    <strong>Commit:</strong> Ejecuta la recuperación destructiva real, sobreescribiendo Supabase.
                                </p>
                                <div className="text-[10px] text-slate-400 italic">Siempre corre un Dry-Run antes de un Commit.</div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Mejores Practicas y Automatizacion */}
                        <div className="bg-indigo-950/30 p-8 rounded-[2rem] border border-indigo-500/20">
                            <h4 className="text-indigo-400 font-black text-sm uppercase tracking-widest mb-4 flex items-center gap-2"><Zap size={18} /> Automatización</h4>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-white text-xs font-bold mb-1">✅ QUÉ ES AUTOMÁTICO:</p>
                                    <p className="text-[11px] text-slate-400 leading-relaxed">El Audit Log, la detección de archivos en disco, la generación de Checkpoints por sistema, y la metadata del runtime se autogestionan.</p>
                                </div>
                                <div>
                                    <p className="text-amber-400 text-xs font-bold mb-1">❌ QUÉ NO ES AUTOMÁTICO:</p>
                                    <p className="text-[11px] text-slate-400 leading-relaxed">Descargar los backups, almacenarlos fuera del servidor y ejecutar un recovery manual.</p>
                                </div>
                                <div className="bg-red-500/10 border border-red-500/20 text-red-300 p-4 rounded-xl text-[11px] font-bold mt-4 leading-relaxed">
                                    ⚠️ ATENCIÓN: Si no descarga o respalda externamente los archivos, un daño físico del equipo o formateo puede destruir los respaldos locales perdiendo todo acceso a la recuperación.
                                </div>
                            </div>
                        </div>

                        {/* Que descargar */}
                        <div className="bg-emerald-950/30 p-8 rounded-[2rem] border border-emerald-500/20">
                            <h4 className="text-emerald-400 font-black text-sm uppercase tracking-widest mb-4 flex items-center gap-2"><Shield size={18} /> ¿Qué debo descargar para protegerme?</h4>
                            <ol className="text-slate-300 text-xs space-y-3 list-decimal ml-4">
                                <li><strong>JSON Full</strong> (Obligatorio, contiene el binario restaurable).</li>
                                <li><strong>Incrementales recientes</strong> (Para recuperar el trabajo del día).</li>
                                <li><strong>Recovery ZIP</strong> (Si deseas descargar todo en 1 solo paquete consolidado).</li>
                                <li><strong>Excel Export</strong> (Solo para uso administrativo y directivo).</li>
                            </ol>
                            <div className="mt-6">
                                <p className="text-[10px] text-emerald-500 font-black uppercase tracking-widest mb-2">FRECUENCIA RECOMENDADA</p>
                                <div className="flex gap-2 flex-wrap">
                                    <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full text-[10px] font-bold">Diario (Incremental)</span>
                                    <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full text-[10px] font-bold">Semanal (JSON Full)</span>
                                    <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full text-[10px] font-bold">Antes de Cambios (Checkpoints)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
